import type en from "../en/settings";
import type { Messages } from "../../types";

const fr: Messages<typeof en> = {
  // Shared
  unknownItem: "Élément inconnu",
  viewPlans: "Voir les forfaits",
  primary: "Principal",
  makePrimary: "Définir comme principal",
  deleteNamed: "Supprimer « {name} » ?",
  addRate: "Ajouter le tarif",
  saveRate: "Enregistrer le tarif",
  addRateSummary: "+ Ajouter un tarif",
  yourRole: "Votre rôle",

  // Shared settings components
  mediaPreviewAlt: "Aperçu : {label}",
  mediaCurrentImage: "Image actuelle",
  mediaReplaceImage: "Remplacer l’image",
  mediaUploadImage: "Importer une image",
  multiSelectSearch: "Rechercher…",
  multiSelectNone: "Aucune sélection",
  multiSelectRemove: "Retirer {label}",
  multiSelectFilter: "{label} : filtrer les options",
  multiSelectNoMatches: "Aucun résultat",
  rateType: "Type de tarif",
  rateKindFlat: "Tarif fixe",
  rateKindWeight: "Selon le poids",
  rateKindPrice: "Selon le montant de la commande",
  rateKindFree: "Livraison gratuite",
  rateKindPickup: "Retrait sur place",
  rateHelpFlat: "Un prix unique pour toutes les commandes de cette zone.",
  rateHelpWeight: "S’applique lorsque le poids total de la commande se situe dans la plage.",
  rateHelpPrice: "S’applique lorsque le sous-total de la commande se situe dans la plage.",
  rateHelpFree: "Aucun frais. Définissez un montant minimum pour « livraison gratuite dès … ».",
  rateHelpPickup: "Le client vient chercher sa commande. Vous pouvez facturer des frais de préparation.",
  rateHandlingFee: "Frais de préparation",
  ratePrice: "Prix",
  rateFreeOver: "Gratuite pour les commandes de plus de",
  rateMinOrder: "Montant minimum de commande",
  rateMaxOrder: "Montant maximum de commande",
  rateNoMinimum: "Aucun minimum",
  rateNoMaximum: "Aucun maximum",
  rateMinWeight: "Poids minimum (grammes)",
  rateMaxWeight: "Poids maximum (grammes)",
  inviteLinkNote: "L’envoi d’e-mails n’est pas configuré sur cette plateforme : partagez vous-même ce lien à usage unique. Il expire dans 7 jours et ne fonctionne que pour l’adresse e-mail invitée.",
  inviteRole: "Rôle",
  inviteSending: "Envoi…",
  inviteSend: "Envoyer l’invitation",

  // Store details
  detailsTitle: "Informations de la boutique",
  detailsDescription: "Informations de base sur votre boutique, utilisées sur votre vitrine, vos factures et vos e-mails.",
  detailsGeneral: "Général",
  detailsStoreName: "Nom de la boutique",
  detailsDescriptionHint: "Un bref résumé de ce que vous vendez (500 caractères max.).",
  detailsContactEmail: "E-mail de contact",
  detailsContactEmailHint: "Affiché aux clients et utilisé comme adresse de réponse pour les e-mails de commande.",
  detailsContactPhone: "Téléphone de contact",
  detailsPlatformAddress: "Adresse sur la plateforme : {url}",
  detailsBusinessAddress: "Adresse de l’entreprise",
  detailsBusinessAddressDesc: "Utilisée sur les factures et affichée dans les coordonnées de votre boutique.",
  detailsLine1: "Adresse (ligne 1)",
  detailsLine2: "Adresse (ligne 2)",
  detailsCity: "Ville",
  detailsRegion: "Région / État",
  detailsPostalCode: "Code postal",
  detailsCountry: "Pays",
  detailsCountryHint: "Le pays d’origine de votre boutique. Également utilisé pour la configuration par défaut de la livraison et des taxes.",
  detailsRegional: "Paramètres régionaux",
  detailsStorefrontLanguage: "Langue de la vitrine",
  detailsTimezone: "Fuseau horaire",
  detailsWeightUnit: "Unité de poids",
  detailsUnitKg: "Kilogrammes (kg)",
  detailsUnitG: "Grammes (g)",
  detailsUnitLb: "Livres (lb)",
  detailsUnitOz: "Onces (oz)",
  detailsLogoFavicon: "Logo et favicon",
  detailsLogo: "Logo",
  detailsFavicon: "Favicon",
  detailsFaviconHint: "Image carrée d’au moins 64×64 px.",
  detailsSearchListing: "Référencement dans les moteurs de recherche",
  detailsSearchListingDesc: "Titre et description par défaut de votre page d’accueil dans les résultats de recherche.",
  detailsSeoTitle: "Titre SEO",
  detailsSeoTitleHint: "Jusqu’à 70 caractères.",
  detailsMetaDescription: "Méta-description",
  detailsMetaDescriptionHint: "Jusqu’à 160 caractères.",
  detailsNoindex: "Masquer ma boutique des moteurs de recherche",
  detailsNoindexHint: "Ajoute une directive noindex. Utile tant que vous construisez encore votre boutique.",
  detailsSave: "Enregistrer les informations",
  detailsSaved: "Informations de la boutique enregistrées",
  currencyTitle: "Devise",
  currencyUses: "Les prix et les rapports utilisent la devise {currency}.",
  currencyChange: "Changer de devise",
  currencyWarning: "Les prix des produits ne sont pas convertis. Vérifiez vos prix après avoir changé de devise.",
  currencyNew: "Nouvelle devise",
  currencyLocked: "La devise ne peut plus être modifiée une fois des commandes passées, afin que votre historique de commandes reste cohérent.",
  currencyChanged: "La devise de la boutique est désormais {currency}. Vérifiez les prix de vos produits : les montants n’ont pas été convertis.",
  planUsage: "Utilisation du forfait",
  planUsageOn: "Vous êtes sur le forfait {plan}.",
  usageProducts: "Produits",
  usageStaff: "Comptes collaborateurs",
  usageDomains: "Domaines personnalisés",
  usageStorage: "Stockage (Mo)",
  managePlan: "Gérer le forfait et la facturation →",
  ownerManagesPlan: "Le propriétaire de la boutique gère le forfait.",

  // Taxes
  taxDescription: "Taux de taxe appliqués lors du paiement.",
  taxDisclaimerTitle: "Les taux de taxe sont saisis par vous",
  taxDisclaimer: "Storelancer applique exactement les taux que vous configurez ici. La plateforme ne détermine pas quelles taxes s’appliquent à votre activité et ne calcule pas automatiquement les taxes locales (comme la TVA, la GST ou la sales tax américaine). Consultez un conseiller fiscal au sujet de vos obligations.",
  taxCalculation: "Calcul des taxes",
  taxPricesInclude: "Les prix incluent les taxes",
  taxPricesIncludeHint: "Courant dans l’UE, au Royaume-Uni et en Australie. La taxe est calculée comme une partie du prix au lieu d’être ajoutée au paiement.",
  taxOnShipping: "Appliquer les taxes à la livraison",
  taxOnShippingHint: "La livraison est taxée selon les taux marqués « s’applique à la livraison ».",
  taxRates: "Taux de taxe",
  taxRatesDesc: "Les taux du pays du client s’appliquent à toutes les adresses de ce pays ; les taux régionaux s’y ajoutent pour les adresses de la région concernée. Les taux correspondants sont additionnés.",
  taxNoRates: "Aucun taux de taxe",
  taxNoRatesDesc: "Sans taux de taxe, aucune taxe n’est facturée lors du paiement.",
  taxColLocation: "Lieu",
  taxColRate: "Taux",
  taxColClass: "Catégorie",
  taxColShipping: "Livraison",
  taxAllProducts: "Tous les produits",
  taxEditRate: "Modifier {name} ({location})",
  taxAddRate: "+ Ajouter un taux de taxe",
  taxNameHint: "Affiché sur les reçus, par ex. « TVA ».",
  taxRegionCode: "Code de région",
  taxRegionCodeHint: "Par ex. CA pour la Californie. Vide = tout le pays.",
  taxRatePercent: "Taux (%)",
  taxRatePercentHint: "Deux décimales maximum.",
  taxClass: "Catégorie de taxe",
  taxClassHint: "S’applique uniquement aux produits de cette catégorie de taxe. Vide = tous les produits taxables (et la livraison).",
  taxAppliesToShipping: "S’applique à la livraison",
  taxAppliesToShippingHint: "Utilisé uniquement lorsque « Appliquer les taxes à la livraison » est activé.",
  taxSettingsSaved: "Paramètres de taxe enregistrés",
  taxUnknownRate: "Taux de taxe inconnu",
  taxInvalidPercent: "Saisissez un pourcentage avec deux décimales au maximum, par ex. 20 ou 7,25.",
  taxInvalidPercentField: "Pourcentage invalide",
  taxRateUpdated: "Taux de taxe mis à jour",
  taxRateAdded: "Taux de taxe ajouté",
  taxRateDeleted: "Taux de taxe supprimé",

  // Checkout
  checkoutDescription: "Définissez ce que les clients doivent fournir lorsqu’ils passent commande.",
  checkoutCustomerInfo: "Informations client",
  checkoutGuest: "Autoriser les commandes sans compte",
  checkoutGuestHint: "Les clients peuvent commander sans créer de compte. Si vous désactivez cette option, un compte sera exigé pour chaque commande, ce qui réduit généralement le taux de conversion.",
  checkoutPhone: "Exiger un numéro de téléphone",
  checkoutPhoneHint: "Utile pour les transporteurs et le paiement à la livraison.",
  checkoutTerms: "Exiger l’acceptation des conditions",
  checkoutTermsHint: "Les clients doivent cocher une case pour accepter vos conditions et politiques. Modifiez-les dans {pages}.",
  checkoutNotes: "Autoriser les notes de commande",
  checkoutNotesHint: "Les clients peuvent vous laisser une note (par ex. des instructions de livraison).",
  checkoutReservation: "Réservation du stock pour les paiements en ligne",
  checkoutReservationText: "Lorsqu’un client est redirigé vers une page de paiement en ligne (Stripe, PayPal), les articles lui sont réservés afin qu’ils ne soient pas épuisés pendant le paiement. Si le paiement n’est pas finalisé dans ce délai, la commande expire et le stock est libéré. Pour les moyens de paiement hors ligne (paiement à la livraison, virement bancaire), le stock reste réservé jusqu’à ce que vous annuliez la commande ou la marquiez comme payée.",
  checkoutReservationTime: "Durée de réservation (minutes)",
  checkoutReservationHint: "Entre 10 et 1440 minutes (24 heures). 30 minutes conviennent à la plupart des boutiques.",
  checkoutSave: "Enregistrer les paramètres de paiement",
  checkoutSaved: "Paramètres de paiement enregistrés",

  // SEO
  seoDescription: "Aidez les moteurs de recherche à trouver votre boutique et gardez vos anciens liens fonctionnels.",
  seoSearchEngines: "Moteurs de recherche",
  seoSitemapText: "Votre boutique publie automatiquement un plan du site (sitemap) et un fichier robots. Soumettez le sitemap dans Google Search Console ou Bing Webmaster Tools.",
  seoStoreWideText: "Le titre, la description et l’option « masquer des moteurs de recherche » de la boutique se trouvent dans {link}. Les produits, collections et pages disposent de leurs propres champs SEO dans leurs éditeurs. Les boutiques non publiées ne sont pas indexées.",
  seoRedirects: "Redirections d’URL",
  seoRedirectsDesc: "Redirigez les visiteurs (et les moteurs de recherche) d’un ancien chemin vers une nouvelle adresse avec une redirection permanente (301). Les redirections ne s’appliquent que lorsque l’ancien chemin n’existe plus sur votre boutique.",
  seoFromPath: "Chemin d’origine",
  seoFromPathHint: "Un chemin de votre boutique, par ex. /ancienne-promo",
  seoTo: "Vers",
  seoToHint: "Un chemin, par ex. /products/nouvel-article, ou une URL https://",
  seoAddRedirect: "Ajouter la redirection",
  seoNoRedirects: "Aucune redirection",
  seoNoRedirectsDesc: "Ajoutez-en une lorsque vous renommez un produit, une collection ou une page pour que les anciens liens continuent de fonctionner.",
  seoColFrom: "De",
  seoColTo: "Vers",
  seoDeleteRedirect: "Supprimer la redirection ?",
  seoDeleteRedirectBody: "{path} ne sera plus redirigé.",
  seoRedirectSaved: "Redirection enregistrée",
  seoRedirectDeleted: "Redirection supprimée",
  seoUnknownRedirect: "Redirection inconnue",

  // Shipping
  shipDescription: "Où vous livrez et ce que les clients paient pour la livraison.",
  shipHowTitle: "Comment la livraison est déterminée",
  shipHow1: "Lors du paiement, le pays du client (et son code postal, si une zone définit des préfixes) détermine la zone. Une zone qui cite explicitement le pays est prioritaire sur « Reste du monde ».",
  shipHow2: "Chaque tarif actif de cette zone dont les conditions correspondent au panier (montant, poids) est proposé ; le client en choisit un.",
  shipHow3: "Si aucune zone ni aucun tarif ne correspond, le client ne peut pas commander d’articles nécessitant une livraison. Les produits numériques et les services n’en nécessitent jamais.",
  shipNoActiveRate: "Vous vendez des produits physiques mais n’avez aucun tarif de livraison actif : les clients ne peuvent pas finaliser leur commande.",
  shipNoZones: "Aucune zone de livraison pour l’instant",
  shipNoZonesDesc: "Créez une zone pour {country} (ou pour tous les pays où vous livrez) et ajoutez au moins un tarif.",
  shipNoZonesAction: "Utilisez « Ajouter une zone de livraison » ci-dessous.",
  shipAddZone: "Ajouter une zone de livraison",
  shipRestOfWorld: "Reste du monde",
  shipKindFlat: "Tarif fixe",
  shipKindWeight: "Selon le poids",
  shipKindPrice: "Selon le montant",
  shipKindFree: "Livraison gratuite",
  shipKindPickup: "Retrait sur place",
  shipZoneName: "Nom de la zone",
  shipZoneNameHint: "Visible uniquement par vous, par ex. « France » ou « Europe ».",
  shipCountries: "Pays",
  shipCountriesHint: "Choisissez « Reste du monde » pour couvrir tous les pays qui ne figurent dans aucune autre zone.",
  shipPostalPrefixes: "Préfixes de code postal",
  shipPostalPrefixesHint: "Limitez la zone aux codes postaux commençant par ces valeurs, séparées par des virgules ou des retours à la ligne (par ex. 75, 92, 93). Laissez vide pour tout le pays.",
  shipSaveZone: "Enregistrer la zone",
  shipCreateZone: "Créer la zone",
  shipRateName: "Nom du tarif",
  shipRateNameHint: "Affiché aux clients lors du paiement.",
  shipRateNamePlaceholder: "Livraison standard",
  shipDeliveryEstimate: "Délai de livraison estimé",
  shipDeliveryEstimatePlaceholder: "2 à 4 jours ouvrés",
  shipActive: "Actif",
  shipActiveHint: "Les tarifs inactifs ne sont jamais proposés lors du paiement.",
  shipOrdersBetween: "Commandes de {min} à {max}",
  shipOrdersOver: "Commandes de plus de {min}",
  shipOrdersUpTo: "Commandes jusqu’à {max}",
  shipWeightRange: "{min} g – {max}",
  shipWeightGrams: "{value} g",
  shipWeightAny: "illimité",
  shipCountriesMore: "{list} et {count} autres",
  shipPostalStarting: "Codes postaux commençant par : {prefixes}",
  shipDeleteZone: "Supprimer la zone",
  shipDeleteZoneBody: "La zone et ses {count} tarifs seront supprimés. Les clients de ces pays ne pourront plus choisir ces tarifs.",
  shipDeleteZoneBody_one: "La zone et ses tarifs ({count}) seront supprimés. Les clients de ces pays ne pourront plus choisir ces tarifs.",
  shipNoRates: "Aucun tarif pour l’instant : les clients de cette zone ne peuvent pas commander d’articles physiques.",
  shipInactive: "Inactif",
  shipNoConditions: "Aucune condition",
  shipFree: "Gratuit",
  shipDeleteRate: "Supprimer le tarif",
  shipEditRate: "Modifier le tarif",
  shipEditZone: "Modifier la zone",
  shipZoneUpdated: "Zone mise à jour",
  shipZoneCreated: "Zone créée : ajoutez-lui maintenant un tarif.",
  shipZoneDeleted: "Zone supprimée",
  shipRateUpdated: "Tarif mis à jour",
  shipRateAdded: "Tarif ajouté",
  shipRateDeleted: "Tarif supprimé",

  // Domains
  domainDescription: "Utilisez votre propre nom de domaine pour votre vitrine.",
  domainStatusPending: "Vérification en attente",
  domainStatusVerified: "Vérifié",
  domainStatusFailed: "Échec de la vérification",
  domainSslPending: "SSL en attente",
  domainSslActive: "SSL actif",
  domainSslFailed: "Échec SSL",
  domainSslUnknown: "SSL inconnu",
  domainPlatformAddress: "Adresse sur la plateforme",
  domainPlatformAddressDesc: "Toujours disponible, gratuite, avec HTTPS inclus.",
  domainConnect: "Connecter un domaine",
  domainUsage: "{used} domaines personnalisés utilisés sur {count} avec le forfait {plan}.",
  domainUsage_one: "{used} domaine personnalisé utilisé sur {count} avec le forfait {plan}.",
  domainNotIncluded: "Les domaines personnalisés ne sont pas inclus dans le forfait {plan}",
  domainUpgrade: "Passez à un forfait supérieur pour connecter un domaine qui vous appartient.",
  domainAtLimit: "Vous avez atteint la limite de domaines de votre forfait. Supprimez un domaine ou changez de forfait pour en ajouter un autre.",
  domainField: "Domaine",
  domainFieldHint: "Par ex. boutique.exemple.com ou exemple.com — vous devez en être propriétaire.",
  domainAdd: "Ajouter le domaine",
  domainNone: "Aucun domaine personnalisé",
  domainNoneDesc: "Votre boutique est accessible via son adresse sur la plateforme.",
  domainLastChecked: "Dernière vérification : {date}",
  domainChecking: "Vérification…",
  domainCheckNow: "Vérifier maintenant",
  domainRemoveTitle: "Supprimer {host} ?",
  domainRemove: "Supprimer le domaine",
  domainRemovePrimaryBody: "Il s’agit de votre domaine principal. Votre boutique repassera sur l’adresse de la plateforme. Pensez à supprimer les enregistrements DNS auprès de votre registraire.",
  domainRemoveBody: "Les visiteurs de ce domaine n’accéderont plus à votre boutique. Pensez à supprimer les enregistrements DNS auprès de votre registraire.",
  domainDnsIntro: "Ajoutez ces enregistrements auprès de votre registraire ou de votre fournisseur DNS. La propagation des modifications peut prendre de quelques minutes à 48 heures.",
  domainVerifyOwnership: "Vérifier la propriété",
  domainPointToStore: "Faire pointer le domaine vers votre boutique",
  domainRootNote: "La plupart des fournisseurs n’autorisent pas de CNAME sur un domaine racine. Utilisez un enregistrement ALIAS/ANAME (ou la valeur d’enregistrement A indiquée), ou connectez plutôt un sous-domaine comme www ou shop.",
  domainRedirecting: "Les visiteurs de ce domaine sont redirigés vers votre domaine principal.",
  domainServing: "Ce domaine affiche directement la boutique (sans redirection).",
  domainStopRedirect: "Arrêter la redirection",
  domainRedirectToPrimary: "Rediriger vers le domaine principal",
  domainDnsType: "Type",
  domainDnsName: "Nom / hôte",
  domainDnsValue: "Valeur",
  domainUnknown: "Domaine inconnu",
  domainAdded: "{host} ajouté. Créez les enregistrements DNS ci-dessous, puis choisissez « Vérifier maintenant ».",
  domainVerifiedHttps: "Vérifié — HTTPS est actif.",
  domainVerifiedPendingSsl: "Vérifié. Le certificat HTTPS est encore en cours d’émission.",
  domainCheckedFailed: "Vérifié à l’instant — échec de la vérification.",
  domainCheckedPending: "Vérifié à l’instant — toujours en attente.",
  domainPrimaryUpdated: "Domaine principal mis à jour",
  domainPlatformPrimary: "L’adresse de la plateforme est désormais l’adresse principale",
  domainRemoved: "Domaine supprimé",

  // Payments
  payDescription: "Connectez vos propres comptes de paiement. L’argent de vos clients est versé directement sur les comptes que vous connectez ici.",
  payStripeConnectedTitle: "Compte Stripe connecté",
  payStripeConnectedBody: "Lancez un test de connexion, puis activez Stripe lors du paiement.",
  payStripeErrorTitle: "Connexion Stripe non finalisée",
  payStripeErrorBody: "La tentative de connexion n’a pas pu aboutir. Veuillez réessayer.",
  payCustomersSee: "Ce que les clients voient actuellement lors du paiement :",
  payTestModeWarning: "Un moyen de paiement en mode test ne peut pas encaisser de vrais paiements. Passez à des identifiants de production avant le lancement.",
  payNoneEnabledTitle: "Les clients ne peuvent pas commander tant que vous n’avez pas activé un moyen de paiement.",
  payNoneEnabledBody: "Connectez un prestataire en ligne ou configurez le paiement à la livraison / le virement bancaire, puis choisissez « Activer au paiement ».",
  payOwnAccountsNote: "Chaque boutique utilise uniquement les comptes de paiement connectés sur cette page. Storelancer ne traite jamais les commandes de votre boutique via un compte de la plateforme et il n’existe aucun compte de secours : si rien n’est activé, le paiement est indisponible. Les frais de transaction sont facturés par votre prestataire.",
  payProblemTitle: "Problème de connexion de paiement",
  payProblemBody: "{providers} nécessitent votre attention. Les moyens de paiement qui échouent à un test de connexion sont automatiquement retirés du paiement.",
  payProblemBody_one: "{providers} nécessite votre attention. Les moyens de paiement qui échouent à un test de connexion sont automatiquement retirés du paiement.",
  payProviders: "Prestataires de paiement",
  payComingLater: "Prochainement",
  payComingLaterDesc: "Ces prestataires ne sont pas encore pris en charge et ne peuvent pas être connectés. Ils sont listés pour vous indiquer ce qui est prévu.",
  payCheckoutSettingsNote: "Besoin de définir quels clients peuvent commander sans compte ou combien de temps le stock est réservé pendant un paiement en ligne ? Consultez les {link}.",
  payCheckoutSettingsLink: "paramètres de paiement",
  payState_not_connected: "Non connecté",
  payState_setup_required: "Configuration requise",
  payState_connected: "Connecté · non activé",
  payState_test_mode: "Actif au paiement · Mode test",
  payState_active: "Actif au paiement",
  payState_connection_problem: "Problème de connexion",
  payState_reauth_required: "Nouvelle autorisation requise",
  payState_unavailable: "Temporairement indisponible",
  payState_not_supported: "Pas encore pris en charge",
  providerName_cod: "Paiement à la livraison",
  providerName_bank_transfer: "Virement bancaire",
  providerDesc_stripe: "Cartes, portefeuilles électroniques et moyens de paiement locaux via Stripe Checkout. Les paiements sont versés sur votre propre compte Stripe.",
  providerDesc_paypal: "Paiement par portefeuille PayPal avec les identifiants de votre propre application REST PayPal Business.",
  providerDesc_cod: "Le client paie le livreur ou vous-même en personne. Vous confirmez le paiement manuellement.",
  providerDesc_bank_transfer: "Affichez vos coordonnées bancaires après la commande. Vous marquez la commande comme payée à réception des fonds.",
  providerDescNotSupported: "Pas encore pris en charge. L’interface d’adaptateur est prête pour cette intégration, mais elle n’est pas implémentée et ne peut donc pas être connectée.",
  providerRegions_stripe: "Disponible dans plus de 45 pays — consultez la liste de Stripe pour le pays de votre entreprise.",
  providerRegions_paypal: "Disponible sur plus de 200 marchés ; les devises prises en charge varient.",
  providerRegions_cod: "Partout où vous pouvez encaisser le paiement à la livraison.",
  providerRegions_bank_transfer: "Partout.",
  providerMethods_stripe: "Cartes, Apple Pay, Google Pay, moyens locaux activés dans votre tableau de bord Stripe",
  providerMethods_paypal: "Portefeuille PayPal, paiement différé / cartes lorsque PayPal les propose",
  providerMethods_cod: "Espèces ou carte à la livraison",
  providerMethods_bank_transfer: "Virement bancaire manuel",
  payModeLive: "Production",
  payModeTest: "Test",
  payDocs: "Documentation ↗",
  payDocsSr: " de {name} (s’ouvre dans un nouvel onglet)",
  payMethods: "Moyens de paiement",
  payRegions: "Régions",
  payAccount: "Compte",
  payViaConnect: " · via Stripe Connect",
  payViaKeys: " · via clés API",
  payLastVerified: "Dernière vérification",
  payCredentials: "Identifiants",
  payCredentialsSaved: "Identifiants enregistrés (chiffrés, jamais réaffichés)",
  payUnavailableTitle: "{name} est temporairement indisponible sur cette plateforme",
  payUnavailableDefault: "La plateforme a suspendu les nouvelles connexions et le paiement pour ce prestataire.",
  paySettingsKept: "Les paramètres existants sont conservés.",
  payLastErrorTitle: "Dernière erreur du prestataire",
  payReauth: "Le prestataire n’accepte plus cette autorisation. Reconnectez le compte ci-dessous.",
  payMissingConfig: "Configuration manquante",
  payStripeKeysIntro: "Dans votre tableau de bord Stripe, ouvrez {path} et créez une {restricted} (recommandé) ou copiez votre clé secrète. Les clés sont vérifiées auprès de Stripe, puis chiffrées. Elles ne sont plus jamais affichées.",
  payStripeKeysPath: "Développeurs → Clés API",
  payStripeRestrictedKey: "clé restreinte",
  payStripeSecretKey: "Clé secrète ou restreinte",
  payStripeSecretKeyHint: "Commence par sk_test_, sk_live_, rk_test_ ou rk_live_. Les clés de test ne créent que des paiements en mode test.",
  payWebhookSigningSecret: "Secret de signature du webhook",
  payWebhookSigningSecretHint: "Commence par whsec_. Vous pouvez l’ajouter après avoir créé le point de terminaison du webhook ci-dessous.",
  payVerifyingStripe: "Vérification auprès de Stripe…",
  payReplaceApiKey: "Remplacer la clé API",
  payVerifySaveKey: "Vérifier et enregistrer la clé",
  payReconnectStripe: "Se reconnecter avec Stripe",
  payConnectStripe: "Se connecter avec Stripe",
  payStripeOauthNote: "Vous vous connecterez à Stripe pour autoriser cette boutique. Les paiements sont versés directement sur votre compte Stripe.",
  payStripeOauthUnavailable: "Stripe Connect en un clic n’est pas configuré sur cette plateforme. Connectez plutôt votre propre compte Stripe avec une clé API.",
  payUseApiKeys: "Utiliser plutôt des clés API",
  payStripeConnectNote: "Connecté via Stripe Connect. Les événements de paiement sont envoyés au webhook Connect de la plateforme et transmis automatiquement à cette boutique.",
  payPaypalIntro: "Créez une application REST sur {path} avec votre compte PayPal {business}, puis collez ses identifiants. Ils sont vérifiés auprès de PayPal et stockés chiffrés.",
  payPaypalPath: "developer.paypal.com → Apps & Credentials",
  payPaypalBusiness: "Business",
  payClientId: "Client ID",
  payClientSecret: "Secret client",
  payEnvironment: "Environnement",
  payEnvironmentHint: "Le mode Sandbox sert aux tests : aucun argent réel n’est transféré.",
  paySandbox: "Sandbox (test)",
  payLive: "Production",
  payWebhookId: "ID du webhook",
  payWebhookIdHint: "Celui du webhook que vous créez dans votre application PayPal.",
  payVerifyingPaypal: "Vérification auprès de PayPal…",
  payReplaceCredentials: "Remplacer les identifiants",
  payVerifySave: "Vérifier et enregistrer",
  payWebhook: "Webhook",
  paySigningSecretSaved: "Secret de signature enregistré",
  payWebhookIdSaved: "ID du webhook enregistré",
  payNotConfigured: "Non configuré",
  payStripeWebhookIntro: "Dans Stripe, ouvrez {path}, collez cette URL et sélectionnez les événements ci-dessous. Copiez ensuite ici le secret de signature du point de terminaison.",
  payStripeWebhookPath: "Développeurs → Webhooks → Ajouter un point de terminaison",
  payPaypalWebhookIntro: "Dans votre application PayPal, ajoutez un webhook avec cette URL et les événements ci-dessous. Collez ensuite ici l’ID du webhook.",
  payCopyUrl: "Copier l’URL",
  payEventsToEnable: "Événements à activer",
  payReplaceSigningSecret: "Remplacer le secret de signature",
  paySigningSecret: "Secret de signature",
  payReplaceWebhookId: "Remplacer l’ID du webhook",
  payWebhookIdPlaceholder: "par ex. 8PT597110X687430LKGECATA",
  payNameAtCheckout: "Nom affiché au paiement",
  payDefaultsTo: "Par défaut : « {name} ».",
  payBankDetails: "Coordonnées bancaires et instructions",
  payBankDetailsHint: "Affichées aux clients après leur commande et dans leur e-mail de confirmation. Indiquez le titulaire du compte, l’IBAN/numéro de compte, le nom de la banque et la référence à utiliser (par ex. le numéro de commande).",
  paySetUp: "Configurer : {name}",
  payEditSettings: "Modifier les paramètres",
  payDisabling: "Désactivation…",
  payDisable: "Désactiver au paiement",
  payEnabling: "Activation…",
  payEnable: "Activer au paiement",
  payTesting: "Test en cours…",
  payTestConnection: "Tester la connexion",
  payDisconnectTitle: "Déconnecter {name} ?",
  payDisconnect: "Déconnecter",
  payDisconnectBody: "Les clients ne verront plus {name} lors du paiement.",
  payDisconnectBodyCredentials: "Les clients ne verront plus {name} lors du paiement et les identifiants enregistrés seront supprimés.",
  payDisconnectInProgress: "Si des clients ont un paiement en cours avec ce prestataire, la déconnexion est bloquée jusqu’à ce qu’il soit finalisé ou expire.",
  payDisconnectRefunds: "Les remboursements des commandes passées payées avec {name} nécessitent de reconnecter le même compte — ou peuvent être effectués depuis le tableau de bord du prestataire.",
  payEnabledNote: "Les clients peuvent choisir ce moyen de paiement (selon les règles ci-dessous).",
  payDisabledNote: "Non proposé aux clients tant que vous ne l’avez pas activé.",
  payRulesSummary: "Règles de paiement (devises, pays, montant de commande)",
  payRulesIntro: "Limitez les cas où ce moyen de paiement est proposé. Laissez une règle vide pour tout autoriser.",
  payCurrencies: "Devises",
  payAllCurrencies: "Toutes les devises",
  payStoreSellsIn: "Votre boutique vend en {currency}.",
  payCustomerCountries: "Pays des clients",
  payAllCountries: "Tous les pays",
  payCountriesHint: "Selon le pays de livraison/facturation du client.",
  payMinAmount: "Montant minimum de commande",
  payMaxAmount: "Montant maximum de commande",
  paySaveRules: "Enregistrer les règles",
  payUnknownConnection: "Connexion de paiement inconnue",
  payUnknownMethod: "Moyen de paiement inconnu",
  payStripeSaved: "Identifiants Stripe vérifiés et enregistrés (mode production). Ensuite : ajoutez le webhook ci-dessous, lancez un test de connexion, puis activez Stripe au paiement.",
  payStripeSavedTest: "Identifiants Stripe vérifiés et enregistrés (mode test). Ensuite : ajoutez le webhook ci-dessous, lancez un test de connexion, puis activez Stripe au paiement.",
  payPaypalSaved: "Identifiants PayPal vérifiés et enregistrés (production). Activez PayPal au paiement quand vous êtes prêt.",
  payPaypalSavedSandbox: "Identifiants PayPal vérifiés et enregistrés (sandbox). Activez PayPal au paiement quand vous êtes prêt.",
  payOfflineSaved: "{name} enregistré. Activez-le avec « Activer au paiement ».",
  payOfflineEnabled: "{name} est désormais proposé au paiement.",
  payTestFailed: "Échec du test de connexion : {reason}. Ce moyen de paiement a été retiré du paiement jusqu’à ce qu’un test réussisse.",
  payTestFailedDefault: "le prestataire a rejeté la requête",
  payTestOkLive: "Connexion OK{account} (mode production).",
  payTestOkTest: "Connexion OK{account} (mode test).",
  payEnabledInCheckout: "Activé au paiement",
  payRemovedFromCheckout: "Retiré du paiement",
  payMaxBelowMin: "Le montant maximum de commande doit être au moins égal au minimum.",
  payMaxBelowMinField: "Doit être au moins égal au minimum",
  payRulesSaved: "Règles de paiement enregistrées",
  payDisconnected: "Déconnecté. Les identifiants enregistrés ont été supprimés.",
  payPasteValue: "Collez la valeur indiquée dans le tableau de bord de votre prestataire.",
  payWebhookSaved: "Paramètre du webhook enregistré",

  // Team
  teamDescription: "Les personnes qui peuvent vous aider à gérer cette boutique, et ce que chacune peut faire.",
  teamInvite: "Inviter quelqu’un",
  teamSeats: "{used} comptes collaborateurs utilisés sur {count} avec le forfait {plan} (le propriétaire n’est pas compté).",
  teamSeats_one: "{used} compte collaborateur utilisé sur {count} avec le forfait {plan} (le propriétaire n’est pas compté).",
  teamNotIncluded: "Les comptes d’équipe ne sont pas inclus dans le forfait {plan}",
  teamUpgrade: "Passez à un forfait supérieur pour inviter des collaborateurs.",
  teamOnlyOwnerAdmins: "Seul le propriétaire de la boutique peut inviter des administrateurs.",
  teamMembers: "Membres",
  teamMemberCount: "{count} membres",
  teamMemberCount_one: "{count} membre",
  teamYou: "(vous)",
  teamRoleFor: "Rôle de {email}",
  teamRemoveTitle: "Retirer {name} ?",
  teamRemoveBody: "Cette personne perdra immédiatement l’accès à cette boutique. Ses actions passées restent dans le journal d’activité.",
  teamPendingInvites: "Invitations en attente",
  teamExpires: "{role} · expire le {date}",
  teamRevokeTitle: "Révoquer l’invitation de {email} ?",
  teamRevoke: "Révoquer",
  teamRolesTitle: "Rôles et autorisations",
  teamRolesDesc: "Les paiements, les remboursements et la gestion de l’équipe sont réservés aux rôles ci-dessous. Le serveur applique ces règles à chaque action.",
  teamRoleDesc_owner: "Accès complet, y compris la facturation, les paiements et la publication. Ne peut pas être retiré.",
  teamRoleDesc_admin: "Tout sauf la facturation : produits, commandes et remboursements, paiements, domaines, équipe et publication.",
  teamRoleDesc_product_manager: "Produits, collections, stock, éditeur de boutique et pages. Peut consulter les statistiques.",
  teamRoleDesc_order_manager: "Commandes (y compris remboursements et expédition), stock et paramètres de livraison. Peut consulter les clients.",
  teamRoleDesc_support: "Consulter les commandes et gérer les clients. Pas de remboursements ni de paramètres.",
  teamRoleDesc_analyst: "Accès en lecture seule aux commandes, aux clients et aux statistiques.",
  teamUnknownMember: "Membre de l’équipe inconnu",
  teamInviteEmailed: "Invitation envoyée par e-mail à {email}.",
  teamInviteCreated: "Invitation créée pour {email}.",
  teamInviteRevoked: "Invitation révoquée",
  teamRoleUpdated: "Rôle mis à jour",
  teamMemberRemoved: "Membre retiré",
};

export default fr;
